import { Spacing } from '@persona-id/design-tokens';
import Fuse from 'fuse.js';
import { compact, findIndex } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import styled from 'styled-components';

import DraggableListItem from 'dashboard/components/DraggableListItem';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { reorder } from 'dashboard/lib/dnd/utils';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';

const AttributeEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[5]};
`;

const SearchContainer = styled.div`
  display: flex;
`;

const StyledSearchInput = styled(SearchInput)`
  width: 100%;
`;

const AttributeHeaderContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.element.borderRadius};

  padding: ${Spacing[3]};
`;

export interface AttributeConfiguration<T = string> {
  label: string;
  key: T;
  display: boolean;
}

export interface AttributeEditorTableProps<T> {
  allAttributes: AttributeConfiguration<T>[];
  moduleAttributes: T[];
  hideEmptyAttributes?: boolean;
  updateAttributes: (attributes: T[]) => void;
  updateHideEmptyAttributes?: (hideEmptyAttributes: boolean) => void;
}

function AttributeEditorTable<T extends string = string>({
  allAttributes,
  moduleAttributes,
  hideEmptyAttributes,
  updateAttributes,
  updateHideEmptyAttributes,
}: AttributeEditorTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeAttributes, setActiveAttributes] = useState<T[]>(moduleAttributes);

  useEffect(() => {
    setActiveAttributes(moduleAttributes);
  }, [moduleAttributes]);

  const wrappedSetActiveAttributes = (activeAttributes: T[]) => {
    setActiveAttributes(activeAttributes);
    updateAttributes(activeAttributes);
  };

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [
        { name: 'label', weight: 0.8 },
        { name: 'key', weight: 0.2 },
      ],
    };

    return new Fuse(allAttributes, fuseOptions);
  }, [allAttributes]);

  const filteredAllAttributes =
    searchTerm === '' ? allAttributes : fuse.search(searchTerm).map(({ item }) => item);

  const unselectedAttributes = useMemo(() => {
    return filteredAllAttributes.filter(
      (attribute) => !(activeAttributes || []).some((attr) => attr === attribute.key),
    );
  }, [filteredAllAttributes, activeAttributes]);

  const filteredActiveAttributes = useMemo(() => {
    return compact(
      activeAttributes.map((attribute) => {
        const attrIdx = findIndex((attr) => attr.key === attribute, filteredAllAttributes || []);
        return attrIdx === -1 ? null : filteredAllAttributes[attrIdx];
      }),
    );
  }, [filteredAllAttributes, activeAttributes]);

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const reorderedAttributes = reorder(activeAttributes, source.index, destination.index);
    wrappedSetActiveAttributes(reorderedAttributes);
  };

  // generateAttributesDnd generates a list of toggleable drag and drop attributes, given a list of attributes
  // to generate from and a toggle function which takes in an attribute and returns a function with no params
  // that toggles the attribute, and also booleans to determine if toggles should be disabled or not
  const generateAttributesDnds = ({
    attributes,
    generateToggle,
    isToggled,
    shouldDisableDrag,
    shouldDisableToggle,
  }: {
    attributes: AttributeConfiguration<T>[];
    generateToggle: (attribute: T) => () => void;
    isToggled: boolean;
    shouldDisableToggle: boolean;
    shouldDisableDrag: boolean;
  }) => {
    return attributes.map((attribute, idx) => {
      const handleToggle = generateToggle(attribute.key);
      const onChange = () => {
        if (shouldDisableToggle) {
          // Do nothing, which essentially disables the toggle.
          // We don't set the `disabled` flag on ToggleSlider because we want the toggle to still appear
          // as if its toggled
        } else {
          handleToggle();
        }
      };

      const toggleComponent = (
        <DraggableListItem
          key={attribute.key}
          draggableId={attribute.key}
          index={idx}
          disableDrag={shouldDisableDrag}
          dataTest={`attribute-editor-table__draggable-item--${attribute.key}`}
          suffix={
            <ToggleSlider
              data-test={`attribute-editor-table__toggle-slider-${attribute.label}`}
              value={isToggled}
              onChange={onChange}
            />
          }
        >
          <Paragraph>{attribute.label}</Paragraph>
        </DraggableListItem>
      );

      return attribute.display ? toggleComponent : null;
    });
  };

  const generateSelectedAttributesToggle = (attribute: T) => () => {
    const updatedAttributes = activeAttributes.filter((attr) => attr !== attribute);
    wrappedSetActiveAttributes(updatedAttributes);
  };

  const activeDndAttributes = generateAttributesDnds({
    attributes: filteredActiveAttributes,
    generateToggle: generateSelectedAttributesToggle,
    isToggled: true,
    shouldDisableToggle: false,
    shouldDisableDrag: false,
  });

  const generateUnselectedAttributesToggle = (attribute: T) => () => {
    const updatedAttributes = activeAttributes.concat(attribute);
    wrappedSetActiveAttributes(updatedAttributes);
  };
  const unselectedDndAttributes = generateAttributesDnds({
    attributes: unselectedAttributes,
    generateToggle: generateUnselectedAttributesToggle,
    isToggled: false,
    shouldDisableToggle: false,
    shouldDisableDrag: true,
  });

  return (
    <AttributeEditorContainer>
      <Heading size='sm'>Fields</Heading>
      <Spacer height={3} />
      <SearchContainer>
        <StyledSearchInput
          title={null}
          value={searchTerm}
          handleInput={setSearchTerm}
          placeholder='Search by field...'
          dataTest='attribute-editor-table__search'
        />
      </SearchContainer>
      <Spacer height={2} />
      <AttributeHeaderContainer>
        <Label size='md' weight='bold'>
          Fields
        </Label>
      </AttributeHeaderContainer>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='attribute-editor-table__droppable-selected'>
          {(provided) => {
            return (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {activeDndAttributes}
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='attribute-editor-table__droppable-unselected'>
          {(provided) => {
            return (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {unselectedDndAttributes}
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
      {updateHideEmptyAttributes && (
        <>
          <Spacer height={2} />
          <Checkbox
            label={<Paragraph>Hide empty fields</Paragraph>}
            checked={hideEmptyAttributes ?? false}
            onChange={(e) => {
              updateHideEmptyAttributes(e.target.checked);
            }}
          />
        </>
      )}
    </AttributeEditorContainer>
  );
}

export default AttributeEditorTable;
